import type { RootState } from "@/store";
import type { EntityType, StorySettingsType } from "@config";
import type { Repeatable } from "@/features/dynamic-variables/repeatable/types";
import {
  areRepeatableTreesEqual,
  buildRepeatableTree,
  getEntityRepeatableChain,
  pruneOrphanRepeatableNodes,
} from "@/features/dynamic-variables/repeatable/scope";
import {
  isTableCellHeader,
  type ITableCell,
  type ITableSettings,
} from "@library-elements/table";
import { getCellKind } from "@library-elements/table/utils/get-cell-kind";
import { createSelector } from "@reduxjs/toolkit";
import memoize from "lodash/memoize";
import { getWholeEntityLayers } from "./utils/getWholeEntityLayers";
import { invertFlatData } from "@/shared/utils/normalizer";
import { hashStoryContent } from "@/providers/rendering-provider/utils/hash-story-content";
import { isPageChanged } from "@/providers/rendering-provider/utils/verify-page-change";

const DEFAULT_ARRAY_REF = [] as Array<unknown>;
const DEFAULT_OBJECT_REF = {} as Record<string, unknown>;

// ======== GENERAL SELECTORS ========
export const getEntities = (state: RootState) => state.story.flatContentLayers;

export const getRestStory = (state: RootState) => state.story.story;
/**
 * Whether the store still holds the document a save was started for. The
 * editor route remounts on template <-> version navigation, but this slice is
 * global and keeps the previous document until the loader replaces it — so a
 * save that finishes after the swap must not mirror its result (version
 * record, contentPointer, history) into the document now on screen.
 */
export const isLoadedDocument = (
  state: RootState,
  scope: { storyId: string; versionId?: string | null },
): boolean =>
  state.story.story?._id === scope.storyId &&
  (state.story.version?._id ?? null) === (scope.versionId ?? null);
export const getSettings = (state: RootState) =>
  state.story.story?.settings as StorySettingsType;
export const getStoryId = (state: RootState) => state.story.story?._id;
export const getOrgIdFromStory = (state: RootState) => state.story.story?.orgId;
export const isStoryLive = (state: RootState) =>
  state.story.story?.status === "Live";
/**
 * A live story edited since its last publish -- the same test Share runs
 * before asking "Update needed before sharing" (usePublish -> isPageChanged),
 * so the header label and that prompt always agree.
 */
export const hasUnpublishedStoryChanges = (state: RootState) =>
  isStoryLive(state) && isPageChanged(state.story.story);
export const isAutoSaveEnabled = (state: RootState) =>
  state.story.isAutoSaveEnabled;
export const getStoryHistory = createSelector(
  getRestStory,
  (story) => story?.history,
);
export const getRedoUndoState = (state: RootState) => state.story.history;
export const getLastChangeId = createSelector(
  getRedoUndoState,
  (history) => history.current?.changeId,
);
export const getPreviewUrl = createSelector(
  getRestStory,
  (story) => story?.previewUrl,
);

export const getPreviewContentHash = createSelector(
  getRestStory,
  (story) => story?.settings?.previewContentHash,
);

export const getCurrentContentHash = createSelector(
  getEntities,
  getRestStory,
  (flat, story) => {
    if (!flat || !flat["root"]) return "";
    const slides = invertFlatData({ flatData: flat });
    return hashStoryContent({ slides, settings: story?.settings || {} });
  },
);

export const getIsInRedoUndo = createSelector(
  getRedoUndoState,
  (history) => history.isInRedoUndo,
);

// Note: the return type is intentionally loose. Several consumers pass the
// result into APIs that historically treated it as `any` (the previous
// lodash.memoize wrapper erased the inferred type). Preserving that shape
// keeps this hot path from cascading type errors into unrelated call sites.
export const getDesignSystem = createSelector(
  getSettings,
  // biome-ignore lint/suspicious/noExplicitAny: preserves previous contract
  (settings): any => settings?.designSystemV2,
);

export const getDesignSystemValue = (key: string) =>
  createSelector(getDesignSystem, (designSystem) => designSystem?.[key]);

/**
 * Slim Brandfetch snapshot persisted by the design-system brand-import flow.
 * Lets the dialog rehydrate the result card without re-hitting Brandfetch.
 */
export const getBrandImport = createSelector(
  getSettings,
  (settings) =>
    (settings as Record<string, unknown> | undefined)?.brandImport ?? null,
);

export const getStoryTitle = createSelector(
  getSettings,
  (settings) => settings?.title,
);

export const getCustomDynamicVariables = createSelector(
  getSettings,
  (settings) => {
    try {
      const fs = settings?.fs;
      if (!fs) return [];
      const variables = JSON.parse(fs["fs-vars"]);
      return variables;
    } catch (e) {
      return [];
    }
  },
);

/** One story-local formula definition (settings.fs["calc-formulas"]). */
export type CalcFormulaDef = { title: string; formula: string };

/**
 * Story-local formula (calc) variable definitions, keyed by variable name.
 * Formulas live only inside the story that defines them — never as org
 * fast-vars (see docs/plans/2026-08-19-story-local-formulas-design.md). The
 * formula-editor writes here; the calc pill decorator + publish manifest read
 * from here.
 */
export const getCalcFormulaDefs = createSelector(
  getSettings,
  (settings): Record<string, CalcFormulaDef> => {
    try {
      const raw = (settings?.fs as Record<string, string> | undefined)?.[
        "calc-formulas"
      ];
      if (!raw) return DEFAULT_OBJECT_REF as Record<string, CalcFormulaDef>;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object"
        ? (parsed as Record<string, CalcFormulaDef>)
        : (DEFAULT_OBJECT_REF as Record<string, CalcFormulaDef>);
    } catch {
      return DEFAULT_OBJECT_REF as Record<string, CalcFormulaDef>;
    }
  },
);

export const getRepeatables = createSelector(getSettings, (settings): Repeatable[] => {
  try {
    const raw = settings?.fs?.["repeatables"];
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
});

/**
 * Repeatable groups nested by containment (`parentKey` derived from the entity
 * tree). Roots only — nested groups hang off `children`.
 *
 * When a deck IS loaded the entity tree is authoritative, so a group whose own
 * entity is gone was deleted and is pruned (with the groups inside it): the
 * `parentKey` stored on the definition is a fallback for the entities-absent
 * case only, and without pruning it would nest a deleted group back under its
 * old parent — a phantom sub-card in every parent row, and its key seeded into
 * every new row. With no deck loaded (the share dialog opened outside the
 * editor) the stored `parentKey` is the only nesting information there is, so
 * the tree is kept exactly as built.
 *
 * `getEntities` is the raw `flatContentLayers`, so this recomputes on EVERY
 * deck mutation; `resultEqualityCheck` hands back the previous array whenever
 * the recomputed tree is structurally identical, keeping the identity stable
 * for consumers that key effects off it (the share dialog's `defaultValues`).
 */
export const getRepeatableTree = createSelector(
  [getRepeatables, getEntities],
  (repeatables, entities) => {
    const entityMap = (entities ?? {}) as Record<string, EntityType>;
    const roots = buildRepeatableTree(repeatables, entityMap);
    const hasDeck = Boolean(entityMap.root?.childIds?.length);
    return hasDeck ? pruneOrphanRepeatableNodes(roots, entityMap) : roots;
  },
  { memoizeOptions: { resultEqualityCheck: areRepeatableTreesEqual } },
);

/**
 * One repeatable group by key, or undefined when it no longer exists.
 * Returns a new selector instance each call — memoize with `useMemo` in
 * components (or call once per key outside render).
 */
export const getRepeatableByKey = (key: string) =>
  createSelector(getRepeatables, (repeatables) =>
    repeatables.find((repeatable) => repeatable.key === key),
  );

/**
 * Repeatable keys enclosing an entity, outermost first, including the entity's
 * own key.
 * Returns a new selector instance each call — memoize with `useMemo` in
 * components (or call once per key outside render).
 */
export const getEntityRepeatableChainSelector = (id: string) =>
  createSelector(getEntities, (entities) =>
    getEntityRepeatableChain(
      id,
      (entities ?? {}) as Record<string, EntityType>,
    ),
  );

export const getCustomColors = createSelector(getSettings, (settings) => {
  return settings?.customColors || ([] as string[]);
});

export const getStoryAnchors = createSelector(getSettings, (settings) => {
  return settings?.anchors || {};
});

export const getSelectedSetting = <T>(key: string) =>
  createSelector(
    getSettings,
    (settings) => settings?.[key as keyof StorySettingsType] as T,
  );

export const getStoryMenu = createSelector(
  getSettings,
  (settings) => settings?.storyMenu,
);

export const getAcceptToken = (state: RootState) =>
  state.story.story?.settings?.acceptToken;

export const getLastUpdatedEmail = (state: RootState) =>
  state.story.story?.lastUpdateUserEmail;
export const getLastUpdatedTime = (state: RootState) =>
  state.story.story?.updatedAt;
export const getIsSaving = (state: RootState) => state.story.isSaving;
export const getPeerGeneratingPreview = (state: RootState) =>
  state.story.peerGeneratingPreview;
export const getIsPublishing = (state: RootState) => state.story.isPublishing;
export const getPublishTriggeredBy = (state: RootState) =>
  state.story.publishTriggeredBy;
export const getActionOnHold = (state: RootState) => state.story.actionOnHold;
export const getFolderId = (state: RootState) => state.story.story?.folderId;

export const getActiveThreadId = (state: RootState) =>
  state.story.activeThreadId;

export const isActiveThreadEntity = (id: string) =>
  createSelector(getActiveThreadId, (activeThreadId) => activeThreadId === id);

export const getLastSessionId = (state: RootState) =>
  state.story.story?.lastSessionId || "";

export const getIsHistoryAvailable = createSelector(
  getStoryHistory,
  (history) => {
    return (
      history &&
      !Array.isArray(history) &&
      typeof history === "object" &&
      Object.keys(history).length > 0
    );
  },
);

export const getContentPointer = createSelector(
  getSettings,
  (settings) => settings?.contentPointer,
);

// ======== GENERAL SELECTORS ========

// ======== ENTITY SELECTORS ========
export const getOverwriteSettingName = (state: RootState) =>
  state.story.overwriteSettingName;

export const getSlideIds = createSelector(
  (state: RootState) => getEntities(state),
  (entities) => entities.root?.childIds || DEFAULT_ARRAY_REF,
);

let _prevVisibleSlideIds: string[] = [];
export const getVisibleSlideIds = createSelector(
  getSlideIds,
  getEntities,
  (slideIds, entities) => {
    const result = slideIds.filter((id) => !entities[id]?.settings?.isHidden);
    if (
      result.length === _prevVisibleSlideIds.length &&
      result.every((id, i) => id === _prevVisibleSlideIds[i])
    ) {
      return _prevVisibleSlideIds;
    }
    _prevVisibleSlideIds = result;
    return result;
  },
);

export const getRegenerateDesignId = (id: string) =>
  createSelector(getEntities, (entities) => {
    const slide = entities[id];
    if (!slide) return false;
    return slide.settings.notionDesignSlideId;
  });

export const getSlideIdsWithoutRewrite = createSelector(
  getSlideIds,
  getEntities,
  (slideIds, entities) => {
    const hasSlideToFilter = slideIds.some(
      (id) => !!entities[id]?.settings.notionMasterSlideId,
    );
    if (!hasSlideToFilter) return slideIds;
    return slideIds.filter((id) => !entities[id]?.settings.notionMasterSlideId);
  },
);

export const getSlideIdsToRewrite = createSelector(
  getSlideIds,
  getEntities,
  (slideIds, entities) => {
    return slideIds.filter((id) => entities[id]?.settings.notionMasterSlideId);
  },
);

export const areAllSlidesNotRewriteable = createSelector(
  getSlideIds,
  getEntities,
  (slideIds, entities) => {
    return slideIds.every((id) => !entities[id]?.settings.notionMasterSlideId);
  },
);

export const areAllSlidesRewriteable = createSelector(
  getSlideIds,
  getEntities,
  (slideIds, entities) => {
    return slideIds.every((id) => !!entities[id]?.settings.notionMasterSlideId);
  },
);

export const getSlideIndex = (id: string) =>
  createSelector(getSlideIds, (slideIds) => slideIds.indexOf(id));

export const getEntityById = (id: string) =>
  createSelector(
    (state: RootState) => getEntities(state),
    (entities) => entities[id] || DEFAULT_OBJECT_REF,
  );

const EMPTY_OBJECT = {};
export const getEntitySettings = <T>(id: string) =>
  createSelector(
    (state: RootState) => getEntities(state),
    (entities): T => (entities[id]?.settings || EMPTY_OBJECT) as T,
  );

export const getEntityChildren = (id: string) =>
  createSelector(
    (state: RootState) => getEntities(state),
    (entities): string[] =>
      (entities[id]?.childIds || DEFAULT_ARRAY_REF) as string[],
  );

export const getEntityIndex = (id: string) =>
  createSelector(
    (state: RootState) => getEntities(state),
    (entities) => {
      const entity = entities[id];
      const parentId = entity?.parentId;
      if (!parentId) return 0;
      const parent = entities[parentId];
      if (!parent) return 0;
      return parent.childIds.indexOf(id);
    },
  );

export const isFirstEntity = (id: string) =>
  createSelector(getEntityIndex(id), (index) => index === 0);

export const isLastEntity = (id: string) =>
  createSelector(getEntityById(id), getEntities, (entity, entities) => {
    const children = entities[entity.parentId as string].childIds || [];
    const index = children.indexOf(id);
    return index === children.length - 1;
  });

export const getEntityType = (id: string) =>
  createSelector(getEntityById(id), (entity) => entity.type);

export const getEntityRepeatableKey = (id: string) =>
  createSelector(
    getEntityById(id),
    (entity): string | undefined =>
      entity?.settings?.repeatableKey as string | undefined,
  );

export const getEntityNameOrType = (id: string) =>
  createSelector(
    getEntityById(id),
    getOverwriteSettingName,
    getEntityType(id),
    (entity, overwriteName, type) =>
      overwriteName || entity?.settings?.name || type,
  );

export const getEntityParentId = (id: string) =>
  createSelector(getEntityById(id), (entity) => entity.parentId);

/**
 * True only when the group's entire parent set is empty (every sibling group
 * lost its elements) AND this group is the set's first child. Used to render
 * the "Add element" placeholder once for an empty set, instead of one
 * placeholder per emptied group.
 */
export const getIsEmptySetAnchorGroup = (id: string) =>
  createSelector(
    (state: RootState) => getEntities(state),
    (entities) => {
      const group = entities[id];
      if (!group) return false;
      const set = entities[group.parentId as string];
      if (!set) return (group.childIds?.length ?? 0) === 0;

      const groupIds = (set.childIds || []) as string[];
      const isSetEmpty = groupIds.every(
        (groupId) => (entities[groupId]?.childIds?.length ?? 0) === 0,
      );
      return isSetEmpty && groupIds[0] === id;
    },
  );
// ======== ENTITY SELECTORS ========

// ======== ACTIVE ENTITY SELECTORS ========
export const getActiveEntity = createSelector(
  (state: RootState) => state.story.activeEntityId,
  (state: RootState) => getEntities(state),
  (activeId, entities) => entities[activeId || ""],
);

export const getActiveEntityId = createSelector(
  getActiveEntity,
  (entity) => entity?.id,
);

export const isEntityActive = (id: string) =>
  createSelector(getActiveEntityId, (active) => active === id);

export const getActiveEntitySettings = createSelector(
  getActiveEntity,
  (entity) => entity?.settings || {},
);

export const getActiveEntityType = createSelector(
  getActiveEntity,
  getOverwriteSettingName,
  (entity, overwriteSettingName) => {
    return (overwriteSettingName ||
      entity?.settings.name ||
      entity?.type) as string;
  },
);

export const getActiveEntityLayerType = createSelector(
  getActiveEntity,
  (entity) => entity?.type,
);

export const getEntitySelectedSetting = <T>(key: string, id?: string) =>
  createSelector(getActiveEntityId, getEntities, (activeId, entities) => {
    const entity = entities[id || activeId || ""];
    return entity?.settings[key] as T;
  });

export const getActiveEntityComboName = createSelector(
  getActiveEntityId,
  getEntities,
  (activeId, entities) => {
    let currentEntity = entities[activeId];
    while (currentEntity && currentEntity.type !== "combo") {
      currentEntity = entities[currentEntity.parentId as string];
    }
    return currentEntity ? currentEntity.settings.name : undefined;
  },
);

export const getActiveEntitySelectedComboSettings = (key: string) =>
  createSelector(getActiveEntityId, getEntities, (activeId, entities) => {
    let currentEntity = entities[activeId];
    while (currentEntity && currentEntity.type !== "combo") {
      currentEntity = entities[currentEntity.parentId as string];
    }
    return currentEntity ? currentEntity.settings[key] : undefined;
  });

export const getParentComboId = createSelector(
  getActiveEntityId,
  getEntities,
  (activeId, entities) => {
    let currentEntity = entities[activeId];
    while (currentEntity && currentEntity.type !== "combo") {
      currentEntity = entities[currentEntity.parentId as string];
    }
    return currentEntity ? currentEntity.id : "";
  },
);

export const getParentActiveCombo = createSelector(
  [getActiveEntityId, getEntities],
  (activeId, entities) => {
    let currentEntity = entities[activeId];
    while (currentEntity && currentEntity.type !== "combo") {
      currentEntity = entities[currentEntity.parentId as string];
    }
    if (!currentEntity || currentEntity.type !== "combo") return;
    return currentEntity;
  },
);

export const getParentActiveSlideId = createSelector(
  [getActiveEntityId, getEntities],
  (activeId, entities) => {
    let currentEntity = entities[activeId];
    while (currentEntity && currentEntity.type !== "slide") {
      currentEntity = entities[currentEntity.parentId as string];
    }
    if (!currentEntity || currentEntity.type !== "slide") return;
    return currentEntity.id;
  },
);

const collaborationEntitySelector = (activeId: string, slideId?: string) => {
  if (!slideId) return { newEntityId: activeId, newSlideId: "" };
  return { newEntityId: activeId, newSlideId: slideId };
};

export const getCollaborationEntity = createSelector(
  [getActiveEntityId, getParentActiveSlideId],
  memoize(collaborationEntitySelector),
);

// ======== ACTIVE ENTITY SELECTORS ========

// ======== SETTINGS SELECTORS ========
export const getActiveSettingId = (state: RootState) =>
  state.story.activeSettingId;

export const isSettingActive = (id: string) =>
  createSelector(
    getActiveSettingId,
    (activeSettingId) => activeSettingId === id,
  );

export const getDynamicProperty = (property?: string) =>
  createSelector(
    getActiveEntitySettings,
    (settings) => settings[property || ""] as string,
  );

export const isValidCondition = (
  condition?: (settings: Record<string, unknown>) => boolean,
) =>
  createSelector(
    getActiveEntitySettings,
    getParentActiveCombo,
    getClosestGroupIndex,
    getDesignSystem,
    (state: RootState) => !!state.story.version,
    (settings, combo, groupIndex, designSystem, isVersion) => {
      if (!condition) return true;
      const comboName = combo?.settings.name;
      // `isVersion` lets a control hide in version mode unless the element
      // opted out (e.g. a payload-driven table's Size control — its rows and
      // columns come from the version data; see config/elements/table.ts).
      const additionalProps = {
        groupIndex,
        isVersion,
      };
      if (comboName === "Tabs") {
        Object.assign(additionalProps, {
          isShowTabsIcons: combo?.settings.isShowTabsIcons,
        });
      }
      return condition({
        ...settings,
        comboName,
        ...additionalProps,
        designSystem,
      });
    },
  );

export const isActiveSettingId = (id: string) =>
  createSelector(
    getActiveSettingId,
    (activeSettingId) => activeSettingId === id,
  );

// ======== SETTINGS SELECTORS ========

// ======== SLIDE SELECTORS ========
export const getSlidesLength = createSelector(
  getSlideIds,
  (slideIds) => slideIds.length,
);

export const fullScreenBlockId = (id: string) =>
  createSelector(
    getEntityChildren(id),
    (state: RootState) => getEntities(state),
    (children, entities) => {
      const fullScreenCombo = children.find(
        (comboId: string) => entities[comboId]?.settings.name === "FullScreen",
      );
      return fullScreenCombo;
    },
  );

export const hasGridInFullScreenBlock = (id: string) =>
  createSelector(
    getEntityChildren(id),
    (state: RootState) => getEntities(state),
    (children, entities) => {
      const fullScreenCombo = children.find(
        (comboId: string) => entities[comboId]?.settings.name === "FullScreen",
      );
      if (!fullScreenCombo) return false;
      const set = entities[fullScreenCombo].childIds[0];
      if (!set) return false;
      const mediaGroup = entities[set].childIds[1];
      if (!mediaGroup) return false;
      const grid = entities[mediaGroup].childIds.find(
        (elementId: string) => entities[elementId].settings.name === "Grid",
      );
      return !!grid;
    },
  );

export const isSlideHidden = (id: string) =>
  createSelector(getEntityById(id), (entity) => entity.settings.isHidden);

export const selectCanHideSlide = createSelector(
  getSlideIds,
  getEntities,
  (slideIds, entities) => {
    let amt = 0;

    for (const id of slideIds) {
      const slide = entities[id];
      if (!slide.settings.isHidden) {
        amt++;
        if (amt > 1) {
          return true;
        }
      }
    }
    return amt > 1;
  },
);

const memoizedSlideWithEntities = memoize(
  (entities?: { root: string; entities: Record<string, EntityType> }) =>
    entities,
);
export const getSlideWithEntities = (id: string) =>
  createSelector(getEntityById(id), getEntities, (entity, entities) => {
    const id = (entity.type === "combo" ? entity.parentId : entity.id) || "";
    const selected = getWholeEntityLayers(id, entities);
    return memoizedSlideWithEntities(selected);
  });

export const getComboWithEntities = (id: string) =>
  createSelector(getEntityById(id), getEntities, (entity, entities) => {
    const selected = getWholeEntityLayers(entity.id, entities);
    return memoizedSlideWithEntities(selected);
  });

export const getClosestActiveSlide = createSelector(
  [getActiveEntityId, getEntities],
  (activeId, entities) => {
    let currentEntity = entities[activeId];
    while (currentEntity && currentEntity.type !== "slide") {
      currentEntity = entities[currentEntity.parentId as string];
    }
    if (!currentEntity || currentEntity.type !== "slide") return;
    return getWholeEntityLayers(currentEntity.id, entities);
  },
);

export const areAllSlidesScreenSizeHidden = (type: "mobile" | "desktop") =>
  createSelector(
    getSlideIds,
    (state: RootState) => getEntities(state),
    (slideIds, entities) => {
      const slidesCount = slideIds.length;

      const hiddenCount = slideIds.reduce((acc: number, id: string) => {
        const slide = entities[id];
        const deviceVisibility = slide.settings.deviceVisibility;
        if (type === "mobile" && deviceVisibility === "showOnMobile")
          return acc + 1;
        if (type === "desktop" && deviceVisibility === "showOnDesktop")
          return acc + 1;
        return acc;
      }, 0);

      return slidesCount === hiddenCount + 1;
    },
  );

// ======== SLIDE SELECTORS ========

// ======== COMBO SELECTORS ========

export const getNarratorElements = createSelector(
  [(state, id) => getEntityById(id)(state), (state, _) => getEntities(state)],
  (entity, entities) => {
    const temp = {} as Record<string, string[]>;
    entity.childIds.forEach((childId: string) => {
      const set = entities[childId];
      const group = entities[set.childIds[1]];
      temp[`set-${childId}-group-${group?.id}`] = group
        ? group["childIds"]
        : [];
    });
    return temp;
  },
);

export const getComboName = (id: string) =>
  createSelector(
    getEntityById(id),
    (state: RootState) => getEntities(state),
    (entity, entities) => {
      let currentEntity = entity;
      while (currentEntity && currentEntity.type !== "combo") {
        currentEntity = entities[currentEntity.parentId as string];
      }
      return currentEntity ? currentEntity.settings.name : undefined;
    },
  );

export const getComboById = (id: string) =>
  createSelector(
    getEntityById(id),
    (state: RootState) => getEntities(state),
    (entity, entities) => {
      let currentEntity = entity;
      while (currentEntity && currentEntity.type !== "combo") {
        currentEntity = entities[currentEntity.parentId as string];
      }
      return currentEntity as EntityType | undefined;
    },
  );

export const getClosestActiveCombo = createSelector(
  [getActiveEntityId, getEntities],
  (activeId, entities) => {
    let currentEntity = entities[activeId];
    if (currentEntity && currentEntity.type === "slide") {
      const combo = entities[currentEntity.childIds[0]];
      if (!combo) return null;
      return getWholeEntityLayers(combo.id, entities);
    }
    while (currentEntity && currentEntity.type !== "combo") {
      currentEntity = entities[currentEntity.parentId as string];
    }
    if (!currentEntity || currentEntity.type !== "combo") return;
    return getWholeEntityLayers(currentEntity.id, entities);
  },
);

export const getInitialTabId = (comboId: string) =>
  createSelector(getEntities, (entities) => {
    const combo = entities[comboId];
    if (!combo) return "";

    const id = combo.childIds.find((childId: string) => {
      const set = entities[childId];
      if (set && set.settings.isTabDefault) return true;
      return false;
    });
    const dynamicVar = combo.settings
      .dynamicVariableDefaultActiveTabIndex as string;
    try {
      const index = Number.parseInt(dynamicVar);
      if (!isNaN(index)) return combo.childIds[index];
    } catch (e) {}

    return id || combo.childIds[0];
  });

export const getActiveComboSets = createSelector(
  getActiveEntityId,
  getEntities,
  (activeId, entities) => {
    const combo = entities[activeId] as EntityType;
    if (!combo || combo.type !== "combo") {
      return [];
    }
    const sets: EntityType[] = [];
    for (const setId of combo.childIds) {
      const set = entities[setId];
      if (set) {
        sets.push(set);
      }
    }
    return sets;
  },
);

// ======== COMBO SELECTORS ========

// ======== SET SELECTORS ========
export const isSingleSet = createSelector(
  [getActiveEntity, getEntities],
  (entity, entities) => {
    if (entity.type !== "set") return false;
    const parent = entities[entity.parentId as string];
    return parent.childIds.length === 1;
  },
);
// ======== SET SELECTORS ========

// ======== GROUP SELECTORS ========
export const getGroupWidth = createSelector(
  [
    (state, id) => getEntityById(id)(state),
    (state: RootState, _) => getEntities(state),
    (_, id) => id,
  ],
  (entity, entities, id) => {
    const parentId = entity.parentId as string;
    const parent = entities[parentId];
    const settings = entities[parentId]?.settings;
    const comboSettings = entities[parent.parentId as string].settings;
    const groupsRatio = (settings.groupsRatio ||
      comboSettings.groupsRatio ||
      []) as string[];
    const index = parent.childIds.indexOf(id);
    const width = groupsRatio[index] || "100";
    return width;
  },
);

export const getMediaOrderInMobile = createSelector(
  [
    (state, id) => getEntityById(id)(state),
    (state: RootState, _) => getEntities(state),
  ],
  (entity, entities) => {
    const child = entity["childIds"][0];
    const childEntitySettings = entities[child]?.settings || {};
    if (entity["childIds"].length !== 1) return 1;
    if (
      childEntitySettings.name === "Video" ||
      childEntitySettings.name === "Image" ||
      childEntitySettings.name === "Charts"
    )
      return 0;
    return 1;
  },
);

export const getClosestGroupIndex = createSelector(
  [getActiveEntityId, getEntities],
  (activeId, entities) => {
    const currentEntity = entities[activeId];
    if (!currentEntity) return -1;
    if (currentEntity.type === "group") {
      const parent = entities[currentEntity.parentId as string];
      if (!parent) return -1;
      return parent.childIds.indexOf(currentEntity.id);
    }
    if (currentEntity.type === "element") {
      const group = entities[currentEntity.parentId as string];
      if (!group) return -1;
      const set = entities[group.parentId as string];
      if (!set) return -1;
      return set.childIds.indexOf(group.id);
    }
  },
);
// ======== GROUP SELECTORS ========

// ======== THUMBNAIL SELECTORS ========
export const getThumbnailId = (state: RootState) =>
  state.story.activeThumbnailId;
export const isThumbnailActive = (id: string) =>
  createSelector(
    getThumbnailId,
    (activeThumbnailId) => activeThumbnailId === id,
  );

export const getThumbnailIndexes = createSelector(
  getSlideIds,
  (state: RootState) => getEntities(state),
  (slideIds, entities) => {
    const indexes = {} as Record<string, number>;
    let index = 0;
    slideIds.forEach((id: string) => {
      const slide = entities[id];
      if (!slide.settings.isHidden) {
        indexes[id] = ++index;
      }
    });
    return indexes;
  },
);

export const getThumbnailIndex = (id: string) =>
  createSelector(getThumbnailIndexes, (indexes) => indexes[id]);
// ======== THUMBNAIL SELECTORS ========

// ======== ELEMENTS LIBRARY SELECTORS ========

const memoizedColorSelector = memoize(
  (entity: EntityType, entities: Record<string, EntityType>) => {
    let currentEntity = entity;
    const backgroundColors: Record<string, unknown> = {};

    while (currentEntity) {
      if (currentEntity.settings?.backgroundColor)
        backgroundColors[currentEntity.type] =
          currentEntity.settings?.backgroundColor;
      currentEntity = entities[currentEntity.parentId || ""];
    }

    return backgroundColors;
  },
);
export const getParentBackground = (id: string) =>
  createSelector(getEntityById(id), getEntities, (entity, entities) =>
    memoizedColorSelector(entity, entities),
  );

const getSelectedClosestGroupElements = memoize(
  (setChildIds: string[], entities: Record<string, any>) => {
    const elements = [] as EntityType[];
    setChildIds.forEach((setId: string) => {
      const group = entities[setId];
      group.childIds.forEach((childId: string) => {
        elements.push(entities[childId]);
      });
    });
    return elements;
  },
);

export const getClosestGroupElements = (id: string, disabled = true) =>
  createSelector(getEntityById(id), getEntities, (entity, entities) => {
    if (!entity) return null;
    const parent = entities[entity.parentId as string];
    if (!parent || parent.type !== "group" || disabled) return null;
    const set = entities[parent.parentId as string];
    if (!set) return null;
    return getSelectedClosestGroupElements(set.childIds, entities);
  });

const getSelectedLayersDataOnAddNewEntity = memoize((entity, entities) => {
  let comboName = "";
  let slideId = "";
  let comboId = "";
  let type = "";

  switch (entity?.type) {
    case "combo": {
      comboName = entity.settings.name as string;
      slideId = entity.parentId as string;
      comboId = entity.id;
      type = "combo";
      break;
    }
    case "element": {
      const parentEntity = entities[entity.parentId || ""];
      const grandParentEntity = entities[parentEntity.parentId || ""];
      const greatGrandParentEntity = entities[grandParentEntity.parentId || ""];
      comboName = greatGrandParentEntity.settings.name as string;
      slideId = greatGrandParentEntity.parentId as string;
      comboId = greatGrandParentEntity.id;
      type = "element";
      break;
    }
    case "group": {
      const parentEntity = entities[entity.parentId || ""];
      const grandParentEntity = entities[parentEntity.parentId || ""];
      comboName = grandParentEntity.settings.name as string;
      slideId = grandParentEntity.parentId as string;
      comboId = grandParentEntity.id;
      type = "element";
      break;
    }
    case "slide": {
      slideId = entity.id;
      type = "combo";
      break;
    }
  }

  return { comboName, slideId, comboId, type };
});

export const getLayersDataOnAddNewEntity = (id: string) =>
  createSelector(getEntityById(id), getEntities, (entity, entities) =>
    getSelectedLayersDataOnAddNewEntity(entity, entities),
  );
// ======== ELEMENTS LIBRARY SELECTORS ========

// =========== VERSION SELECTORS ===========
export const getVersion = (state: RootState) => state.story.version;
export const isVersionInEditor = createSelector(
  getVersion,
  (version) => !!version,
);
export const getVersionId = createSelector(
  getVersion,
  (version) => version?._id,
);
export const getVersionData = createSelector(
  getVersion,
  (version) => version?.data,
);
export const getShortVersionUrl = createSelector(
  getVersion,
  (version) => version?.shortUrl,
);
export const hasVersionPDF = createSelector(
  getVersion,
  (version) => !!version?.data?.downloadablePdf,
);
export const getVersionCreatorId = createSelector(
  getVersion,
  (version) => version?.userId,
);
// =========== VERSION SELECTORS ===========

// Send flow P5 (docs/plans/2026-09-29-send-flow-access-contract.md): the
// caller's role and guest flag. The stored object, replaced only by
// setDocAccess (once per load), so selecting it re-renders nobody meanwhile.
export const getDocAccess = (state: RootState) => state.story.docAccess;

export const getStoryStatus = createSelector(
  isVersionInEditor,
  getRestStory,
  (isVersionInEditor, story) => {
    if (isVersionInEditor) return "Version";
    return story?.status || "Draft";
  },
);

export const getIsSelectedStatus = (status: string) =>
  createSelector(getStoryStatus, (storyStatus) => storyStatus === status);

export const getLiveTitle = createSelector(
  getStoryTitle,
  (pageTitle) => pageTitle,
);

export const getCurrentPayloadId = createSelector(
  getStoryId,
  getVersionId,
  (storyId, versionId) => versionId || storyId,
);

// ======== TABLE ELEMENT ========
export const getTableActiveCellsIds = (state: RootState) => {
  return state.story.activeTableCellsIds;
};

export const getTableActiveStartCellId = (state: RootState) => {
  return state.story.activeTableCellsIds[0] ?? null;
};

export const getTableFormulaEdit = (state: RootState) =>
  state.story.tableFormulaEdit;

export const getIsTableCellSelected = createSelector(
  getActiveEntityType,
  getTableActiveStartCellId,
  (type, cellId) => {
    return type === "Table" && cellId != null;
  },
);

export const getIsTableCellToolbar = createSelector(
  getTableActiveStartCellId,
  getActiveEntityType,
  (cellId, entityType) => {
    return cellId != null && entityType === "Table";
  },
);

const getActiveStartCell = createSelector(
  getTableActiveStartCellId,
  getActiveEntity,
  (cellId, entity): ITableCell | null => {
    if (!cellId || entity?.settings.name !== "Table") return null;
    const { tableData } = entity.settings as unknown as ITableSettings;
    return tableData.find((el) => el.id === cellId) ?? null;
  },
);

// True only when the active table selection is a single calc cell.
export const getIsActiveCalcCell = createSelector(
  getActiveStartCell,
  getTableActiveCellsIds,
  (startCell, activeCellsIds): boolean => {
    if (activeCellsIds.length > 1) return false;
    if (!startCell) return false;
    return getCellKind(startCell) === "calc";
  },
);

export const getActiveCalcFormatHtml = createSelector(
  getActiveStartCell,
  getIsActiveCalcCell,
  (startCell, isCalc) => (isCalc ? (startCell?.calcFormatHtml ?? null) : null),
);

export const getTableActiveCellProperty = <T>(property: keyof ITableCell) =>
  createSelector(
    getTableActiveStartCellId,
    getActiveEntity,
    (cellId, entity) => {
      if (!cellId || entity?.settings.name !== "Table") {
        return null;
      }
      const { tableData } = entity.settings as unknown as ITableSettings;
      const cell = tableData.find((el) => el.id === cellId);
      if (!cell) return null;
      return cell[property] as T;
    },
  );

const getTableActiveCellDetails = createSelector(
  getTableActiveStartCellId,
  getActiveEntity,
  (cellId, entity) => {
    if (!cellId || entity?.settings.name !== "Table") {
      return null;
    }

    const settings = entity.settings as unknown as ITableSettings;
    const {
      tableData,
      tableCols,
      tableRows,
      tableHasHeader,
      tableHeaderPosition,
      tableHasAlternatingRows,
    } = settings;

    const cellIdx = tableData.findIndex((el) => el.id === cellId);
    if (cellIdx < 0) return null;
    const cell = tableData[cellIdx];

    const isHeader = isTableCellHeader({
      cellIdx,
      tableCols: tableCols,
      tableRows: tableRows,
      tableHasHeader: tableHasHeader,
      tableHeaderPosition: tableHeaderPosition,
    });

    const row = Math.floor(cellIdx / tableCols);
    const isAlt = tableHasAlternatingRows && row % 2 !== 0;

    return { settings, cellIdx, cell, isHeader, isAlt };
  },
);

export const getTableActiveCellBgColor = createSelector(
  getTableActiveCellDetails,
  (details) => {
    if (!details) {
      return "#ffffff";
    }
    const {
      settings: {
        tableHeaderBgColor,
        tableAlternatingRowsBgColor,
        tableCellBgColor,
      },
      cell,
      isHeader,
      isAlt,
    } = details;

    if (cell.bgColor) {
      return cell.bgColor;
    }

    if (isHeader) {
      return tableHeaderBgColor;
    }

    if (isAlt) {
      return tableAlternatingRowsBgColor;
    }

    return tableCellBgColor;
  },
);

export const getTableActiveCellBorderColor = createSelector(
  getTableActiveCellDetails,
  (details) => {
    if (!details) {
      return "#aaaaaa";
    }
    const {
      settings: {
        tableHeaderBorderColor,
        tableAlternatingRowsBorderColor,
        tableCellBorderColor,
      },
      cell,
      isHeader,
      isAlt,
    } = details;

    if (cell.borderColor) {
      return cell.borderColor;
    }

    if (isHeader) {
      return tableHeaderBorderColor;
    }

    if (isAlt) {
      return tableAlternatingRowsBorderColor;
    }

    return tableCellBorderColor;
  },
);

// ===============================

// ======== IMAGE ELEMENT ========
export const isAllowedToCropImage = createSelector(
  getActiveEntityId,
  getEntities,
  (activeId, entities) => {
    const TYPES = {
      DISABLED: "disabled",
      ENABLED: "enabled",
    };
    if (!activeId || !entities[activeId]) return TYPES.DISABLED;

    const entity = entities[activeId];
    if (!entity.parentId || !entities[entity.parentId]) return TYPES.DISABLED;

    const group = entities[entity.parentId];
    if (!group.parentId || !entities[group.parentId]) return TYPES.DISABLED;

    const set = entities[group.parentId];
    if (!set.parentId || !entities[set.parentId]) return TYPES.DISABLED;

    const groupIndex = set.childIds.indexOf(group.id);

    const combo = entities[set.parentId];

    // ===== DISABLE ON FULLSCREEN ANIMATION ======
    if (
      combo.settings.name === "FullScreen" &&
      groupIndex === 1 &&
      entity.settings.animation
    ) {
      return TYPES.DISABLED;
    }
    if (["Image", "Logo"].includes(entity.settings.name as string)) {
      return TYPES.ENABLED;
    }
    return TYPES.DISABLED;
  },
);
// ======== IMAGE ELEMENT ========

// ======== TABLES 2.0 IN-CELL ELEMENTS ========

// Per-table derived matrix: cellId → ordered element child ids. Order comes
// from table.childIds (the single source of order — nothing extra persisted).
// Manually cached per table so an unchanged mapping keeps a stable identity
// across unrelated entity edits (entities' reference changes every keystroke).
const cellChildrenCache = new Map<
  string,
  { childIds: string[]; cellIds: string[]; result: Record<string, string[]> }
>();
export const getTableCellChildrenIndex = memoize((tableId: string) =>
  createSelector(getEntities, (entities): Record<string, string[]> => {
    const table = entities[tableId];
    const childIds = table?.childIds ?? [];
    const cellIds = childIds.map(
      (childId) => (entities[childId]?.settings.cellId as string) ?? "",
    );
    const cached = cellChildrenCache.get(tableId);
    if (
      cached &&
      cached.childIds.length === childIds.length &&
      cached.cellIds.length === cellIds.length &&
      cached.childIds.every((id, i) => id === childIds[i]) &&
      cached.cellIds.every((id, i) => id === cellIds[i])
    ) {
      return cached.result;
    }
    const result: Record<string, string[]> = {};
    childIds.forEach((childId, i) => {
      const cellId = cellIds[i];
      if (!cellId) return;
      (result[cellId] ??= []).push(childId);
    });
    cellChildrenCache.set(tableId, {
      childIds: [...childIds],
      cellIds,
      result,
    });
    return result;
  }),
);

// { tableId, cellId } of the ACTIVE in-cell element, or null when the active
// entity is not an element child of a table. Cached so the identity is stable
// while the owner is unchanged.
let cachedInCellOwner: { tableId: string; cellId: string } | null = null;
export const selectActiveInCellOwner = createSelector(
  getActiveEntityId,
  getEntities,
  (activeId, entities): { tableId: string; cellId: string } | null => {
    const active = entities[activeId || ""];
    const cellId = active?.settings.cellId as string | undefined;
    const table = entities[active?.parentId || ""];
    const owner =
      active && cellId && table?.settings.name === "Table"
        ? { tableId: table.id, cellId }
        : null;
    if (
      owner?.tableId === cachedInCellOwner?.tableId &&
      owner?.cellId === cachedInCellOwner?.cellId
    ) {
      return cachedInCellOwner;
    }
    cachedInCellOwner = owner;
    return owner;
  },
);

export const selectIsActiveInCellElement = (state: RootState): boolean =>
  selectActiveInCellOwner(state) !== null;

// Per-id variant; used to disable slide-level chrome/drag for in-cell elements.
export const selectIsEntityInCell = memoize((id: string) =>
  createSelector(getEntities, (entities): boolean => {
    const entity = entities[id];
    if (!entity || !entity.settings.cellId) return false;
    return entities[entity.parentId || ""]?.settings.name === "Table";
  }),
);
// ======== TABLES 2.0 IN-CELL ELEMENTS ========
